import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import NProgress from 'nprogress';

import { authApi } from '@/utilities/axios';
import { useAuthStore } from '@/modules/auth/store';
import { showToast } from '@/utilities/toast-handler';
import { CompanyType } from '../enum';
import { hasCompanyFeatures } from '@/product/ams/utils';

const COMPANY_URL = process.env.VUE_APP_API_VERSION + '/admin/companies';

const COMPANY_TYPES = [
    { id: '1', name: CompanyType.FIRE_AND_RESCUE },
    { id: '2', name: CompanyType.POLICE },
    { id: '3', name: CompanyType.NHS },
];

export const useCompanyStore = defineStore('company', () => {
    const authStore = useAuthStore();
    const company_id = computed(() => {
        return authStore.getLoggedUserCompanyId;
    });
    const activeCompany = ref(useLocalStorage('activeCompany', {}));
    const loading = ref(false);
    const editId = ref(null);
    const company = ref({
        name: null,
        type: null,
        logo_id: null, // atm logo upload is not yet configured
    });
    const companies = ref([]);
    const companyTypes = ref(COMPANY_TYPES);

    // const toast = useToast();
    watch(activeCompany, () => {
        if (activeCompany.value?.id) {
            switchActiveCompany();
        }
    });

    // helper functions
    // this is only for root user where company is required to be switched since root user doesnt come with a company id
    function switchActiveCompany() {
        // set root selected company
        authStore.selectCompany(activeCompany.value?.id);
    }

    function getCompanyType(typeId) {
        return COMPANY_TYPES?.find((type) => type.id == typeId)?.name;
    }

    function cleanCache() {
        activeCompany.value = {};
    }

    function initCompanyDetails() {
        company.value = {
            name: null,
            type: null,
            logo_id: null, // atm logo upload is not yet configured
        };
        editId.value = null;
    }
    // actions
    const getCompanies = async () => {
        try {
            NProgress.start();
            loading.value = true;
            const response = await authApi.get(`${COMPANY_URL}`);
            if (response.statusCode === 200) {
                companies.value = response.data;
                loading.value = false;
                return companies.value;
            }
        } catch (error) {
            showToast({}, 'Failed to fetch companies');
        } finally {
            NProgress.done();
        }
    };

    const addCompany = async (company) => {
        try {
            NProgress.start();
            const response = await authApi.post(`${COMPANY_URL}`, company);
            if (response.statusCode === 201) {
                showToast(response.toast, 'Company added successfully');
                companies.value.push(response.data);
            }
            return response.data;
        } catch (error) {
            showToast({}, error.data.message ?? 'Failed to add company');
        } finally {
            NProgress.done();
        }
    };

    const editCompany = (data) => {
        // console.log(company);
        editId.value = data.id;
        company.value.name = data.name;
        company.value.type = data.type;
        company.value.logo_id = data.logo_id;
    };

    const updateCompany = async (id, company) => {
        try {
            NProgress.start();
            const response = await authApi.patch(`${COMPANY_URL}/${id}`, company);
            if (response.statusCode === 200) {
                showToast(response.toast, 'Company updated successfully');
                // debugger;
                // replace the updated company inside the companies array
                const index = companies.value.findIndex((company) => company.id === response.data.id);
                // Once the matching index is found, replace the company with the updated one
                if (index !== -1) {
                    companies.value[index] = response.data;
                }
            }
            return response.data;
        } catch (error) {
            showToast({}, error.data.message ?? 'Failed to update company');
        } finally {
            NProgress.done();
        }
    };

    const deleteCompany = async (id) => {
        try {
            NProgress.start();
            const response = await authApi.delete(`${COMPANY_URL}/${id}`);
            if (response.statusCode === 200) {
                showToast(response.toast, 'Company deleted');
                companies.value = companies.value?.filter((company) => company.id !== id);
            }
        } catch (error) {
            showToast({}, error.data.message ?? 'Failed to delete company');
        } finally {
            NProgress.done();
        }
    };
    
     function useFeatureAccess(featureKeys) {
        return computed(() => {
            return hasCompanyFeatures(company_id.value, featureKeys);
        });
    }


    return {
        // states
        loading,
        activeCompany,
        editId,
        company,
        companies,
        companyTypes,
        // actions
        getCompanies,
        addCompany,
        editCompany,
        updateCompany,
        deleteCompany,
        useFeatureAccess,
        // helpers
        getCompanyType,
        initCompanyDetails,
        cleanCache,
    };
});
